import { BaseAPIClient } from "./baseApi";
import { installMessagesApi } from "./messagesApi";

const POST = "POST";
const GET = "GET";
const PUT = "PUT";
const PATCH = "PATCH";
const DELETE = "DELETE";

export class PitchApi extends BaseAPIClient {
  validateEmail(email) {
    return this.call("/check-email", GET, email);
  }

  signIn(credentials) {
    return this.call("/sign-in", POST, credentials);
  }

  signUp(customerData) {
    return this.call("/sign-up", POST, customerData);
  }

  requestRecoverPassword(email) {
    return this.call("/recover", POST, { email });
  }

  validateOtp(otp) {
    return this.call(`/valid/${otp}`, GET);
  }

  updatePassword(data) {
    return this.call("/password", POST, data);
  }

  // Apenas confere o link: a conta nao e ativada aqui. O token e obrigatorio --
  // sem ele a API recusa, porque o link antigo (so com o id) nunca expirava.
  activateAccount(id, token) {
    return this.call(
      `/validate-email/${id}`,
      GET,
      token ? { token } : undefined
    );
  }

  // Ativacao do auto-cadastro: a senha ja existe, entao confirmar o codigo do
  // email e o unico passo que falta.
  confirmActivation(data) {
    return this.call("/activate", POST, data);
  }

  showProfile() {
    return this.call("/profile", GET);
  }

  getDashboard() {
    return this.call("/dashboard", GET);
  }

  getWallet() {
    return this.call("/wallet", GET);
  }

  getFinance(params) {
    return this.call("/finance", GET, params);
  }

  // Pitch Coins (card 17trt6jd8uu): so leitura.
  getCoinsSummary() {
    return this.call("/coins/summary", GET);
  }

  getCoinsStatement(params) {
    return this.call("/coins/statement", GET, params);
  }

  getCoinsEarnGuide() {
    return this.call("/coins/earn", GET);
  }

  // Campanhas em andamento para o professor (destaque da pagina).
  getCoinCampaigns() {
    return this.call("/coins/campaigns", GET);
  }

  // Etapa 4: ranking geral de professores do semestre.
  getCoinRanking() {
    return this.call("/coins/ranking", GET);
  }

  // Etapa 4: selos com o andamento de cada um.
  getCoinBadges() {
    return this.call("/coins/badges", GET);
  }

  getCoinBenefits() {
    return this.call("/coins/benefits", GET);
  }

  getCoinRedemptions() {
    return this.call("/coins/redemptions", GET);
  }

  requestCoinRedemption(benefitId) {
    return this.call("/coins/redemptions", POST, { benefitId });
  }

  getCoinReferrals() {
    return this.call("/coins/referrals", GET);
  }

  createCoinReferral(email, target) {
    return this.call("/coins/referrals", POST, { email, target });
  }

  cancelCoinRedemption(id) {
    return this.call(`/coins/redemptions/${id}/cancel`, POST);
  }

  removerDisponibilidade(id) {
    return this.call(`/profile/update/${id}`, DELETE);
  }

  updateProfile(data) {
    return this.call("/profile", PUT, data);
  }

  // Switch "receber novos alunos" do perfil: vale na hora, sem o formulário.
  // Idioma do Connect salvo no professor (seletor da barra lateral).
  updateLanguage(language) {
    return this.call("/profile/language", PATCH, { language });
  }

  // Tema do Connect ("light" | "dark"), salvo no usuario como o idioma.
  updateTheme(theme) {
    return this.call("/profile/theme", PATCH, { theme });
  }

  updateAcceptingStudents(acceptingNewStudents) {
    return this.call("/profile/accepting-students", PUT, {
      acceptingNewStudents,
    });
  }

  getPartnerPlans() {
    return this.call("/plans", GET);
  }

  createPartnerPlan(data) {
    return this.call("/plans", POST, data);
  }

  updatePartnerPlan(id, data) {
    return this.call(`/plans/${id}`, PUT, data);
  }

  // Retorno de 30/10: liga/desliga o plano no match de alunos novos.
  setPlanVisibility(id, showToNewStudents) {
    return this.call(`/plans/${id}/visibility`, PUT, { showToNewStudents });
  }

  deletePartnerPlan(id) {
    return this.call(`/plans/${id}`, DELETE);
  }

  getPlanLinkedStudents(id) {
    return this.call(`/plans/${id}/linked-students`, GET);
  }

  getNotifications(page, category, unreadOnly = false) {
    const filter = category ? `&category=${encodeURIComponent(category)}` : "";
    const unread = unreadOnly ? "&unread=true" : "";
    return this.call(
      `/notifications?page=${page}&size=10${filter}${unread}`,
      GET
    );
  }

  getUnreadNotificationsCount() {
    return this.call("/notifications/unread-count", GET);
  }

  readNotification(id) {
    return this.call(`/notifications/${id}`, PUT);
  }

  unreadNotification(id) {
    return this.call(`/notifications/${id}/unread`, PUT);
  }

  // Card 86akj8ptp: o professor também recebe comunicação da Central; o
  // clique no botão entra na métrica dela.
  clickNotification(id) {
    return this.call(`/notifications/${id}/click`, PUT);
  }

  // Card 86akqxu0h: formulários de pesquisa enviados pela escola.
  getPendingSurveys() {
    return this.call("/surveys/pending", GET);
  }

  getSurvey(id) {
    return this.call(`/surveys/${id}`, GET);
  }

  submitSurvey(id, answers) {
    return this.call(`/surveys/${id}/responses`, POST, { answers });
  }

  // "Salvar" sem enviar: respostas até aqui e a página (seção) em que parou.
  saveSurveyDraft(id, answers, sectionId = null) {
    return this.call(`/surveys/${id}/draft`, PUT, { answers, sectionId });
  }

  getStudyGroups() {
    return this.call("/groups", GET);
  }

  // O que a mudança de frequência vai exigir: escolher horário novo, escolher
  // qual manter, ou nada (card 86ak6kqa1).
  getFrequencyChangePlan(groupId, frequency) {
    return this.call(`/groups/${groupId}/frequency/plan`, GET, { frequency });
  }

  // Aulas extras no fim do nível (card 86ajzet19). A prévia e a confirmação
  // mandam só a quantidade — as datas são recalculadas no servidor, para que
  // uma prévia desatualizada não vire aula sobreposta.
  previewExtraLessons(groupId, quantity) {
    return this.call(`/groups/${groupId}/extra-lessons/preview`, GET, {
      quantity,
    });
  }

  createExtraLessons(groupId, data) {
    return this.call(`/groups/${groupId}/extra-lessons`, POST, data);
  }

  realignSubjects(groupId, eventId) {
    return this.call(`/groups/${groupId}/lessons/${eventId}/realign`, POST);
  }

  declineExtraLessons(promptId) {
    return this.call(`/extra-lesson-prompts/${promptId}/decline`, PUT);
  }

  getOneStudyGroups(id) {
    return this.call("/groups/" + id, GET);
  }

  getStudyGroupAttendance(id, params) {
    return this.call(`/groups/${id}/attendance`, GET, params);
  }

  updateStudyGroupAttendance(groupId, data) {
    return this.call(`/groups/${groupId}/attendance`, PUT, data);
  }

  getGroupLessonRoster(groupId, params) {
    return this.call(`/groups/${groupId}/lessons`, GET, params);
  }

  // Card 17trt6jfxt2: aulas encerradas com presença ainda não confirmada.
  getPendingAttendance() {
    return this.call(`/attendance/pending`, GET);
  }

  saveLessonAttendance(groupId, eventId, data) {
    return this.call(
      `/groups/${groupId}/attendance/lesson/${eventId}`,
      PUT,
      data
    );
  }

  updateStudentEnglishLevel(groupId, studentId, data) {
    return this.call(
      `/groups/${groupId}/students/${studentId}/level`,
      PUT,
      data
    );
  }

  // Card 17trt6jda8c: troca de turma do aluno entre turmas do professor.
  studentTransferOptions(groupId, studentId) {
    return this.call(
      `/groups/${groupId}/students/${studentId}/transfer-options`,
      GET
    );
  }

  transferStudent(groupId, studentId, targetClassId) {
    return this.call(`/groups/${groupId}/students/${studentId}/transfer`, PUT, {
      targetClassId,
    });
  }

  updateStudentBilling(groupId, studentId, data) {
    return this.call(
      `/groups/${groupId}/students/${studentId}/billing`,
      PUT,
      data
    );
  }

  listStudyGroups() {
    return this.call("/groups", GET);
  }

  listGroupMonthlyCharges(groupId, params = {}) {
    return this.call(`/groups/${groupId}/monthly-charges`, GET, params);
  }

  updateGroupMonthlyCharge(groupId, chargeId, data) {
    return this.call(
      `/groups/${groupId}/monthly-charges/${chargeId}`,
      PUT,
      data
    );
  }

  updateGroupChargeStatus(groupId, chargeId, status) {
    return this.call(
      `/groups/${groupId}/monthly-charges/${chargeId}/status`,
      PATCH,
      { status }
    );
  }

  udpateRequest(id, data) {
    return this.call(`/groups/${id}`, PUT, data);
  }

  updateRequest(id, data) {
    return this.udpateRequest(id, data);
  }

  listCourseContent() {
    return this.call("/course", GET);
  }

  getCourseById(id) {
    return this.call(`/course/${id}`, GET);
  }

  getCourseContent(module, id) {
    return this.call(`/course/${module}/${id}`, GET);
  }

  // Card 86akptec0: conclusão das aulas de treinamento do professor.
  setTrainingCompletion(classId, completed) {
    return this.call(`/course/${classId}/complete`, PUT, { completed });
  }

  // Card 86akptec0: evolução do professor nos treinamentos (perfil/materiais).
  getTrainingProgress() {
    return this.call(`/trainings/progress`, GET);
  }

  listCourseNotes(contentId) {
    return this.call(`/course/${contentId}/notes`, GET);
  }

  createCourseNote(contentId, data) {
    return this.call(`/course/${contentId}/notes`, POST, data);
  }

  updateCourseNote(noteId, note, fileIds, extraContentIds) {
    // `fileIds` ausente = não mexe nos anexos; array vazio remove todos. O
    // mesmo vale para `extraContentIds` (card 17trt6jfxtn).
    const payload = { note };
    if (fileIds !== undefined) payload.fileIds = fileIds;
    if (extraContentIds !== undefined)
      payload.extraContentIds = extraContentIds;
    return this.call(`/course/notes/${noteId}`, PUT, payload);
  }

  // Passo 1 do anexo: a API valida tipo/tamanho e devolve a URL assinada de
  // escrita no bucket, junto com o id que será vinculado à anotação.
  requestMaterialUploadUrl(file) {
    return this.call("/files/direct-upload", POST, {
      originalname: file.name,
      mimetype: file.type,
      size: file.size,
    });
  }

  // Passo 2: sobe o arquivo direto no bucket, fora do axios da API — a URL
  // assinada já carrega a autenticação e não pode receber os headers da API.
  uploadMaterialToSignedUrl(url, file) {
    return fetch(url, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    }).then((res) => {
      if (!res.ok) throw new Error("upload failed");
      return res;
    });
  }

  deleteCourseNote(noteId) {
    return this.call(`/course/notes/${noteId}`, DELETE);
  }

  shareCourseNote(noteId, data) {
    return this.call(`/course/notes/${noteId}/shares`, POST, data);
  }

  revokeCourseNoteShare(noteId, customerId) {
    return this.call(`/course/notes/${noteId}/shares/${customerId}`, DELETE);
  }

  calendar() {
    return this.call(`/calendar`, GET);
  }

  createComplementaryClass(data) {
    return this.call(`/calendar/events`, POST, data);
  }

  postergate(id, data) {
    return this.call(`/calendar/events/${id}`, PUT, data);
  }

  // Arrastar a aula no calendario: muda so o horario ({ date, hour }).
  moveEvent(id, data) {
    return this.call(`/calendar/events/${id}/move`, PUT, data);
  }

  // Mudanca definitiva do dia da turma: abre a votacao com escopo permanente.
  postergatePermanent(id, data) {
    return this.call(`/calendar/events/${id}/permanent`, PUT, data);
  }

  cancelarAula(id, motivo) {
    return this.call(`/calendar/events/${id}/cancel`, PUT, motivo);
  }

  adiarParaProxima(id, data) {
    return this.call(`/calendar/events/${id}/next`, PUT, { data });
  }

  getClassContentByLevel(level) {
    return this.call(`/content/by-level`, GET, { level });
  }

  // Chamada das aulas de conversação que este professor conduz.
  getGeneralClassRoster(sessionId) {
    return this.call(`/general-classes/${sessionId}/attendance`, GET);
  }

  // Busca de alunos para incluir na chamada de conversação/monitoria. A API
  // aplica rate limit nesta rota; o chamador deve usar debounce.
  searchGeneralClassStudents(sessionId, search) {
    return this.call(`/general-classes/${sessionId}/eligible-students`, GET, {
      search,
    });
  }

  saveGeneralClassAttendance(sessionId, attendances) {
    return this.call(`/general-classes/${sessionId}/attendance`, PUT, {
      attendances,
    });
  }

  rearrange(eventId, classId) {
    return this.call(`/calendar/events/${eventId}/${classId}/rearrange`);
  }

  confirmClass(eventId) {
    return this.call(`/calendar/events/confirm/${eventId}`, PUT);
  }

  vote(id, option) {
    return this.call(`/calendar/vote/${id}`, POST, { agreed: option });
  }

  // Discordâncias dos alunos com mudanças de aula feitas por este professor.
  // Sem `status`, a API devolve só as pendentes.
  listClassChangeDisputes(params = {}) {
    return this.call("/calendar/change-disputes", GET, params);
  }

  // `reschedule: true` = vou remarcar; false = mantenho o horário.
  resolveClassChangeDispute(disputeId, data) {
    return this.call(`/calendar/change-disputes/${disputeId}`, PUT, data);
  }

  updateStudyGroupLevel(groupId, data) {
    return this.call(`/groups/${groupId}/level`, PUT, data);
  }

  updateStudyGroupFormat(groupId, data) {
    return this.call(`/groups/${groupId}/format`, PUT, data);
  }

  updateStudyGroupFrequency(groupId, data) {
    return this.call(`/groups/${groupId}/frequency`, PUT, data);
  }

  updateStudyGroupKids(groupId, data) {
    return this.call(`/groups/${groupId}/kids`, PUT, data);
  }

  acceptExperimentalTrial(groupId, data) {
    return this.call(`/groups/${groupId}/experimental`, PUT, data);
  }

  // Card 86aje36nz: horários em que professor e aluno podem, para o professor
  // sugerir outra data quando não puder no dia pedido.
  getExperimentalSlotOptions(groupId) {
    return this.call(`/groups/${groupId}/experimental/options`, GET);
  }

  // Card 86aje36nz: horários de match para vincular o aluno da experimental.
  getExperimentalLinkOptions(groupId) {
    return this.call(`/groups/${groupId}/experimental/link-options`, GET);
  }

  suggestExperimentalTrial(groupId, data) {
    return this.call(`/groups/${groupId}/experimental/suggest`, PUT, data);
  }

  updateStudentLevel(groupId, customerId, data) {
    return this.call(
      `/groups/${groupId}/students/${customerId}/level`,
      PUT,
      data
    );
  }

  listGroupAttendance(groupId, customerId, params = {}) {
    return this.call(`/groups/${groupId}/attendance`, GET, {
      customerId,
      ...params,
    });
  }

  markStudentAttendance(groupId, eventId, data) {
    return this.call(`/groups/${groupId}/attendance/${eventId}`, PUT, data);
  }

  setStudentLessonCompletion(groupId, eventId, customerId, completed) {
    return this.call(`/groups/${groupId}/lessons/${eventId}/complete`, PUT, {
      customerId,
      completed,
    });
  }

  updateLessonSubject(groupId, eventId, courseClassId) {
    return this.call(`/groups/${groupId}/lessons/${eventId}/subject`, PUT, {
      courseClassId,
    });
  }

  // Card 17trt6jbjf2: bloco abaixo da chamada na aba Presenca.
  getLessonHomework(groupId, eventId) {
    return this.call(`/groups/${groupId}/lessons/${eventId}/homework`, GET);
  }

  // Retorno de 14/10: "Planejamento de aula" da turma (Visão geral), no lugar
  // da observação por aula.
  getClassPlan(groupId) {
    return this.call(`/groups/${groupId}/plan`, GET);
  }

  saveClassPlan(groupId, plan) {
    return this.call(`/groups/${groupId}/plan`, PUT, { plan });
  }

  getStudentSubjectSequence(groupId, customerId) {
    return this.call(`/groups/${groupId}/students/${customerId}/progress`, GET);
  }

  setStudentSubjectProgress(groupId, customerId, data) {
    return this.call(
      `/groups/${groupId}/students/${customerId}/progress`,
      PUT,
      data
    );
  }

  getPartnerFinance(params = {}) {
    return this.call("/finance", GET, params);
  }

  updateBankAccount(payload) {
    return this.call("/bank-account", PUT, payload);
  }

  // Card 86akng14v: convite de cadastro ("Vincular aluno").
  createStudentInvite(payload) {
    return this.call("/student-invites", POST, payload);
  }

  createGroupStudentInvite(groupId, email) {
    return this.call(`/groups/${groupId}/student-invites`, POST, { email });
  }

  listStudentInvites(params = {}) {
    return this.call("/student-invites", GET, params);
  }

  cancelStudentInvite(id) {
    return this.call(`/student-invites/${id}`, DELETE);
  }

  // Exercicios dos alunos (card 17trt6jcyfb): acompanhar, ajustar e comentar.
  getGroupExercises(groupId) {
    return this.call(`/groups/${groupId}/exercises`, GET);
  }

  // Exercicios da aula (:courseClassId) com o gabarito, so leitura
  // (retorno de 18/10).
  getLessonAnswerKey(courseClassId) {
    return this.call(`/exercises/lesson/${courseClassId}`, GET);
  }

  getLessonGames(courseClassId) {
    return this.call(`/exercises/lesson/${courseClassId}/games`, GET);
  }

  // Retorno de 22/10: nos treinamentos o professor faz o exercício.
  listTrainingExercises(courseClassId) {
    return this.call(`/exercises/training/${courseClassId}`, GET);
  }

  getTrainingExercise(exerciseId) {
    return this.call(`/exercises/training/exercise/${exerciseId}`, GET);
  }

  saveTrainingExerciseDraft(exerciseId, responses) {
    return this.call(`/exercises/training/exercise/${exerciseId}/draft`, PUT, {
      responses,
    });
  }

  finishTrainingExercise(exerciseId, responses) {
    return this.call(
      `/exercises/training/exercise/${exerciseId}/finish`,
      POST,
      { responses }
    );
  }

  // Alunos Autonomy de quem e tutor.
  getTutoredExercises() {
    return this.call("/exercises/tutored", GET);
  }

  getStudentExerciseAttempts(customerId) {
    return this.call(`/students/${customerId}/exercises`, GET);
  }

  getExerciseAttempt(attemptId) {
    return this.call(`/exercise-attempts/${attemptId}`, GET);
  }

  setExerciseCorrection(attemptId, itemId, correct) {
    return this.call(
      `/exercise-attempts/${attemptId}/items/${itemId}/correction`,
      PUT,
      { correct }
    );
  }

  saveExerciseFeedback(attemptId, text) {
    return this.call(`/exercise-attempts/${attemptId}/feedback`, PUT, {
      text,
    });
  }

  // Retorno de 16/10: "Marcar como revisado" (false desfaz).
  setExerciseReviewed(attemptId, reviewed) {
    return this.call(`/exercise-attempts/${attemptId}/reviewed`, PUT, {
      reviewed,
    });
  }
}

// Card 17trt6jbffe: metodos de Mensagens (shared/messagesApi.js).
installMessagesApi(PitchApi);

const api = new PitchApi(process.env.VUE_APP_PITCH_API_PARTNER);

export default api;
